import "pdfjs-dist/web/pdf_viewer.css";

import * as pdfjs from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { Loader2, Minus, MoveHorizontal, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import type { Annotation } from "@/lib/api";
import { ensureFreshToken, forceRefresh } from "@/lib/auth-token";
import { useAnnotationTool } from "@/stores/annotation-tool";

import { AnnotationToolbar } from "./AnnotationToolbar";
import { drawPageAnnotations } from "./annotation-overlay";
import { loadFabric, PageEditor, type EditorOpts, type PersistHandlers } from "./fabric-editor";
import {
  editorOpts,
  useAnnotationStyle,
  useEditorToolSync,
} from "./use-editor-tool-sync";
import type { InkModel } from "./ink-model";
import { fingerDrawActive } from "./input-policy";
import { isInkTool, PenDownBuffer } from "./pen-buffer";
import { isPenStrokeActive } from "./stylus-state";
import { useViewportGestures } from "./use-viewport-gestures";
import { clamp, ViewportController } from "./viewport-controller";
import { flushViewState, loadViewState, rememberedTool, saveViewState, type ReaderViewState } from "./view-state";
import { useViewportScrollbars } from "./viewport-scrollbars";
import { OVERLAY_BAND_MARGIN_PX, VIRTUALIZE_MIN_PAGES } from "./visible-band";

// One worker for the whole app — pdf.js parses/rasterizes off the main thread.
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

const MIN_SCALE = 0.5;
const MAX_SCALE = 3; // Phase 1 keeps the current raster envelope; Phase 2 (tiling) lifts it.
const TOP_GAP = 16; // px above the first page when fitted

export interface PageBox {
  n: number;
  top: number;
  h: number;
  w: number;
}

/** Lay every page out top-to-bottom from cached intrinsic sizes at `scale` (px),
 * returning each page's box + the world content size. Pure (no DOM) so the
 * layout math is unit-testable; the caller sizes/reconciles the divs. */
export function computePageLayout(
  sizes: { w: number; h: number }[],
  count: number,
  scale: number,
  gap: number,
): { layout: PageBox[]; maxW: number; contentH: number } {
  const layout: PageBox[] = [];
  let y = 0;
  let maxW = 0;
  for (let n = 1; n <= count; n++) {
    const s = sizes[n - 1] ?? { w: 612, h: 792 };
    const w = s.w * scale;
    const h = s.h * scale;
    layout.push({ n, top: y, h, w });
    maxW = Math.max(maxW, w);
    y += h + gap;
  }
  return { layout, maxW, contentH: Math.max(0, y - gap) };
}

/** Decide which page numbers to add/remove so the rendered set matches `count`.
 * `present` is the pages currently in the DOM. Only a page-count change (a new
 * document) ever removes; a same-document re-raster reuses every div — which is
 * what keeps the long-lived editors attached (north-star A). Pure, for tests. */
export function reconcilePageList(
  present: number[],
  count: number,
): { add: number[]; remove: number[] } {
  const have = new Set(present);
  const add: number[] = [];
  for (let n = 1; n <= count; n++) if (!have.has(n)) add.push(n);
  const remove = present.filter((n) => n < 1 || n > count);
  return { add, remove };
}

export interface PdfSelection {
  text: string;
  page: number;
  rect: { left: number; top: number; width: number };
}

/** A zoomable, pannable PDF.js viewer with a real selectable text layer. Zoom is
 *  a continuous, pointer-anchored CSS transform over a "world" of pages
 *  (see docs/architecture/frontend/zoom-and-raster-pipeline.md): the live
 *  transform is GPU-cheap and only the COMMITTED resolution (`rasterScale`) triggers a pdf.js re-raster, once
 *  motion settles — so pinch/zoom is smooth instead of snapping per step. */
export function PdfViewer({
  docId,
  url,
  onSelect,
  annotations = [],
  annotationsLoading = false,
  editable = false,
  fonts = [],
  persist,
  model,
  chromeless = false,
}: {
  /** The document being viewed — keys its persisted per-doc view state (F8). */
  docId: string;
  url: string;
  onSelect: (sel: PdfSelection | null) => void;
  /** The saved-markup fetch, fed to the model as seed (first) / reconcile (later).
   * The model — NOT this array — is the source of truth for editors + overlays. */
  annotations?: Annotation[];
  /** True while the saved markup is still loading. Editors wait for it so they
   * never seed empty when edit mode is entered before the GET resolves. */
  annotationsLoading?: boolean;
  /** Whether the edit toolbar + Fabric overlay are available (Milestone B). */
  editable?: boolean;
  fonts?: string[];
  persist?: PersistHandlers;
  /** The per-document InkModel (north-star C): editors seed from it, read-only
   * overlays render from it, and its events route to the owning page editor. */
  model: InkModel;
  /** Fullscreen ("borderless paper"): hide the page/zoom toolbar — gestures
   * and keyboard still navigate. */
  chromeless?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // The transformed "world" all pages live in; the controller writes its matrix.
  const worldRef = useRef<HTMLDivElement | null>(null);
  const docRef = useRef<pdfjs.PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState(0);
  // The scale pages are RASTERIZED at (the render key). The live view scale lives
  // in the controller; the CSS transform carries the delta between them.
  const [rasterScale, setRasterScale] = useState(1.1);
  // Live scale for the toolbar % readout (updated, throttled, from the controller).
  const [displayScale, setDisplayScale] = useState(1.1);
  // Keep the page fitted to the viewport width on open AND on later layout /
  // visibility changes. Manual zoom turns this off; the Fit button turns it back
  // on. `fitPendingRef` asks the next render to re-centre at fit width.
  const autoFitRef = useRef(true);
  const fitPendingRef = useRef(true);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  // True once the first raster layout for the CURRENT document has been applied —
  // the mount signal for editors. Reset on url change so a new document re-seeds
  // its editors after ITS first layout. Editors do NOT depend on later re-rasters
  // (north-star A): a zoom commit resizes living editors in place, never rebuilds.
  const [layoutReady, setLayoutReady] = useState(false);
  // Ref twin, read inside renderAll to know whether this is the FIRST layout for
  // the doc (→ a clean rebuild) or a same-doc re-raster (→ reconcile in place).
  const layoutReadyRef = useRef(false);
  // Keep the latest onSelect without re-subscribing the DOM handler.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  // The InkModel is the source of truth for editors + overlays; keep it in a ref
  // so the async render loop / DOM handlers read the current one without re-running.
  const modelRef = useRef(model);
  modelRef.current = model;
  // The live raster scale read inside the async render loop without re-running it.
  const rasterScaleRef = useRef(rasterScale);
  rasterScaleRef.current = rasterScale;
  // Per-page intrinsic sizes (scale 1), cached at load so page layout/visibility
  // can be computed without rasterizing — lets a commit render only the visible
  // pages first (fast, sharp) and fill the rest in on demand.
  const pageSizesRef = useRef<{ w: number; h: number }[]>([]);
  // In-flight pdf.js render tasks (cancelled when the committed scale changes, so
  // a superseded zoom never clogs the worker and stalls the next sharpen).
  const renderTasksRef = useRef<Set<{ cancel: () => void }>>(new Set());
  // Page-render keys (`n@scale`) currently in flight, to dedupe concurrent fills.
  const renderingRef = useRef<Set<string>>(new Set());
  // Debounce handle for the pan-driven "fill newly-visible pages" pass.
  const fillTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // The view-transform controller (declared early so the rasterize/fill helpers
  // below can read it). Constructed once in the block further down.
  const controllerRef = useRef<ViewportController | null>(null);

  // Edit mode: a tool is selected AND editing is allowed. When on, the Fabric
  // overlay owns the pages; when off, the read-only SVG overlay renders.
  // One subscription set, shared by all three host surfaces.
  const style = useAnnotationStyle();
  const { tool } = style;
  const editMode = editable && !!persist && tool !== "none";
  const editModeRef = useRef(editMode);
  editModeRef.current = editMode;
  // Latest tool, read by the pen-down buffer's gate without re-arming listeners.
  const toolRef = useRef(tool);
  toolRef.current = tool;
  // Refs read by the once-constructed controller callbacks + long-lived buffer:
  // the doc id (keys view state) and whether editing is available.
  const docIdRef = useRef(docId);
  docIdRef.current = docId;
  const editableRef = useRef(editable && !!persist);
  editableRef.current = editable && !!persist;
  // The saved per-doc view state to restore at first layout (F8), consumed once.
  const restoreRef = useRef<ReaderViewState | null>(null);
  // Live editor options, so an editor built while Fabric is still loading (or on a
  // later re-seed) picks up the CURRENT tool/style — not a value captured when the
  // async import started (CanvasView already uses this live-ref pattern).
  const optsRef = useRef<EditorOpts>(editorOpts(style));
  optsRef.current = editorOpts(style);
  // Live editors keyed by page number, so a raster commit can resize each in
  // place (north-star A) instead of rebuilding it.
  const editorsRef = useRef<Map<number, PageEditor>>(new Map());
  /** The loaded Fabric module, so a page entering the band can mount an editor
   *  without re-importing. */
  const fabricRef = useRef<Awaited<ReturnType<typeof loadFabric>> | null>(null);
  // Bridges the first-edit dead zone: records a pen contact while edit mode is on
  // but the editors haven't attached yet, then replays it into them (F1.3).
  const penBufferRef = useRef<PenDownBuffer | null>(null);
  if (!penBufferRef.current) penBufferRef.current = new PenDownBuffer();

  // Which page editor owns a client point (its .pdf-page div contains it) — routes
  // a buffered pen contact to the right page when the editors attach.
  const scheduleReconcileRef = useRef<() => void>(() => {});

  /** Mount an editor for every page in the viewport band, drop the rest. Called on
   *  entry to write mode and whenever the camera settles. */
  const reconcileEditors = () => {
    const fabric = fabricRef.current;
    const world = worldRef.current;
    if (!fabric || !world || !persist || !editModeRef.current) return;
    // Never rebuild under an active pen: destroy() would finalize the stroke early.
    if (isPenStrokeActive()) {
      scheduleReconcile();
      return;
    }
    const all = [...world.querySelectorAll<HTMLElement>(".pdf-page")];
    // A document that fits in the band gets every editor, and none of the band
    // machinery: see VIRTUALIZE_MIN_PAGES.
    const virtualizing = all.length > VIRTUALIZE_MIN_PAGES;
    const want = new Map<number, HTMLElement>();
    for (const el of all) {
      if (!virtualizing || pageInViewport(el)) want.set(Number(el.dataset.page), el);
    }
    let changed = false;
    for (const [n, ed] of editorsRef.current) {
      if (want.has(n)) continue;
      ed.destroy();
      editorsRef.current.delete(n);
      changed = true;
    }
    for (const [n, el] of want) {
      if (editorsRef.current.has(n)) continue;
      // Seed from the MODEL, never the query cache — even a rebuilt editor
      // gets the fresh in-session ink, so the seed-race (#5b) can't strand it.
      editorsRef.current.set(n, new PageEditor(fabric, el, n, modelRef.current.byPage(n), persist, optsRef.current));
      changed = true;
    }
    if (changed && virtualizing) drawOverlays();
  };

  const reconcileTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const reconcileFnRef = useRef<() => void>(() => {});
  reconcileFnRef.current = reconcileEditors;
  scheduleReconcileRef.current = scheduleReconcile;
  /** Re-pick the band after the camera settles. Mounting an editor per frame during
   *  a fling would cost far more than the mount it saves. */
  function scheduleReconcile() {
    const n = worldRef.current?.querySelectorAll(".pdf-page").length ?? 0;
    if (n <= VIRTUALIZE_MIN_PAGES) return;
    if (reconcileTimerRef.current !== undefined) clearTimeout(reconcileTimerRef.current);
    reconcileTimerRef.current = setTimeout(() => {
      reconcileTimerRef.current = undefined;
      reconcileFnRef.current();
    }, 60);
  }

  const editorAtClientPoint = (cx: number, cy: number): PageEditor | null => {
    const world = worldRef.current;
    if (!world) return null;
    for (const el of world.querySelectorAll<HTMLElement>(".pdf-page")) {
      const r = el.getBoundingClientRect();
      if (cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom) {
        const n = Number(el.dataset.page);
        const live = editorsRef.current.get(n);
        if (live) return live;
        // The contact landed outside the band — mount that one page now rather than
        // drop the stroke.
        const fabric = fabricRef.current;
        if (!fabric || !persist || !editModeRef.current) return null;
        const ed = new PageEditor(fabric, el, n, modelRef.current.byPage(n), persist, optsRef.current);
        editorsRef.current.set(n, ed);
        drawOverlays();
        return ed;
      }
    }
    return null;
  };

  // Track which page sits under the viewport centre, for the toolbar indicator.
  const updatePageIndicator = () => {
    const root = containerRef.current;
    const world = worldRef.current;
    if (!root || !world) return;
    const box = root.getBoundingClientRect();
    const mid = box.top + box.height / 2;
    let current = 1;
    for (const el of world.querySelectorAll<HTMLElement>(".pdf-page")) {
      if (el.getBoundingClientRect().top <= mid) current = Number(el.dataset.page);
    }
    setPage(current);
  };

  // Custom scrollbars for the transformed viewport (no native scroll): the hook
  // owns the track/thumb DOM + drag-to-pan; `updateScrollbars` (stable) repaints
  // the thumbs from the controller metrics and is driven by onChange below.
  const { updateScrollbars, scrollbars } = useViewportScrollbars(controllerRef);

  // Rasterize one page's canvas + text layer into its (already sized) div, at the
  // given committed scale. Idempotent + deduped; tracks its render task so a
  // superseded scale can cancel it. A stale result (scale moved on) is discarded.
  const rasterizePage = async (pageEl: HTMLElement, n: number, scale: number): Promise<void> => {
    const doc = docRef.current;
    if (!doc) return;
    if (pageEl.dataset.rscale === String(scale)) return; // already sharp at this scale
    const key = `${n}@${scale}`;
    if (renderingRef.current.has(key)) return;
    renderingRef.current.add(key);
    try {
      const dpr = window.devicePixelRatio || 1;
      const pdfPage = await doc.getPage(n);
      const viewport = pdfPage.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      // Fill the page div rather than pin to this raster's px size: if the div is
      // resized on a zoom commit before this page re-rasters, a stale-scale canvas
      // soft-fills (slightly soft) instead of misaligning (the F4a policy).
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const task = pdfPage.render({
        canvasContext: ctx,
        viewport,
        transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
      });
      renderTasksRef.current.add(task);
      try {
        await task.promise;
      } finally {
        renderTasksRef.current.delete(task);
      }
      const textLayerDiv = document.createElement("div");
      textLayerDiv.className = "textLayer";
      const textLayer = new pdfjs.TextLayer({
        textContentSource: pdfPage.streamTextContent(),
        container: textLayerDiv,
        viewport,
      });
      await textLayer.render();
      // The committed scale may have moved on while we were rendering. Don't
      // discard this finished result — that strands a blank placeholder whose only
      // recovery is the user panning. Append it anyway and tag the page with the
      // scale it was ACTUALLY rendered at: since that won't match the current
      // committed scale, the standing fillVisiblePages pass sees the page as dirty
      // and re-renders it sharp — slightly soft for a moment beats permanently blank.
      // Clear any prior raster layers first so a re-render never stacks a second
      // canvas/text layer (Fabric's canvases are wrapped/classed, so untouched).
      for (const c of pageEl.querySelectorAll(":scope > canvas:not(.fabric-preview-layer)")) c.remove();
      pageEl.querySelector(":scope > .textLayer")?.remove();
      pageEl.prepend(canvas); // canvas sits behind the text/annotation layers
      // Insert the text layer BEFORE the Fabric wrapper / preview so their higher
      // z-index (wrap 4, preview 5) keeps the ink overlay on top; append when no
      // editor is mounted (reading mode).
      const fabricLayer = pageEl.querySelector(":scope > .canvas-container, :scope > .fabric-preview-layer");
      if (fabricLayer) pageEl.insertBefore(textLayerDiv, fabricLayer);
      else pageEl.appendChild(textLayerDiv);
      pageEl.dataset.rscale = String(scale);
      // Draw read-only markup once the page is attached (skipped in edit mode —
      // Fabric owns it; the offscreen pre-swap pass leaves it to drawOverlays).
      if (pageEl.isConnected && !editModeRef.current) {
        try {
          drawPageAnnotations(pageEl, modelRef.current.byPage(n));
        } catch {
          /* markup isolation */
        }
      }
    } catch {
      /* render cancelled (scale changed) or failed — leave the placeholder */
    } finally {
      renderingRef.current.delete(key);
    }
  };

  // Whether a page div sits within (or near) the viewport — the shared visibility
  // test for on-demand rasterizing AND overlay drawing, so both virtualize the
  // same way. (Bug B: don't render every page's ink synchronously; only what's on
  // screen.) MARGIN pre-renders a little beyond the edges so a pan reveals ready
  // content.
  const pageInViewport = (el: HTMLElement, margin = 320): boolean => {
    const root = containerRef.current;
    if (!root) return false;
    const box = root.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    return r.bottom > box.top - margin && r.top < box.bottom + margin;
  };

  // Render any page that is in (or near) the viewport but not yet sharp at the
  // committed scale — the on-demand half of virtualization. Only runs once the
  // view is settled (factor ≈ 1); during an active zoom the commit handles it.
  const fillVisiblePages = () => {
    const world = worldRef.current;
    const root = containerRef.current;
    const controller = controllerRef.current;
    if (!world || !root || !controller) return;
    if (Math.abs(controller.factor - 1) > 0.02) return;
    const scale = rasterScaleRef.current;
    for (const el of world.querySelectorAll<HTMLElement>(".pdf-page")) {
      if (!pageInViewport(el)) continue;
      if (el.dataset.rscale !== String(scale)) {
        void rasterizePage(el, Number(el.dataset.page), scale); // draws its overlay on raster
      } else if (!editModeRef.current) {
        // Already sharp + on screen: refresh its read overlay from the CURRENT
        // model — it may have changed (e.g. a cross-device reconcile) while the
        // page was off-screen and thus skipped by drawOverlays. Idempotent + cheap
        // (one page), and it closes the progressive-render staleness gap.
        try {
          drawPageAnnotations(el, modelRef.current.byPage(Number(el.dataset.page)));
        } catch {
          /* markup isolation */
        }
      }
    }
  };

  // Create the controller once. onCommit asks for a re-raster at the settled
  // scale; canCommit defers it while a pen stroke is mid-flight so a page is
  // never re-mounted under an active stroke. onChange (throttled) keeps the % +
  // page indicator current and fills pages a pan brings into view.
  if (!controllerRef.current) {
    let raf = 0;
    controllerRef.current = new ViewportController({
      min: MIN_SCALE,
      max: MAX_SCALE,
      onCommit: (s) => setRasterScale(s),
      canCommit: () => !isPenStrokeActive(),
      onChange: () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const c = controllerRef.current;
          if (c) setDisplayScale(c.scale);
          updatePageIndicator();
          updateScrollbars();
          // Persist the per-doc zoom/camera/tool (debounced in view-state) so a
          // refresh restores where the user was (F8).
          if (c) {
            saveViewState(docIdRef.current, {
              zoom: c.scale, tx: c.tx, ty: c.ty, tool: toolRef.current, updatedAt: Date.now(),
            });
          }
          clearTimeout(fillTimerRef.current);
          fillTimerRef.current = setTimeout(fillVisiblePages, 120);
          // The band moved, so the live editor set may have to move with it.
          scheduleReconcileRef.current();
        });
      },
    });
  }

  // Arm the pen-down buffer for the whole annotatable session (F1.3 + F1.4). Reads
  // live refs, so both the standing arm effect and the re-arm on editor teardown
  // install the same behavior: buffer the first-edit dead zone, and — in read mode
  // — auto-activate the remembered tool on the first PEN contact.
  const armPenBuffer = () => {
    const container = containerRef.current;
    const buffer = penBufferRef.current;
    if (!container || !buffer || !editableRef.current) return;
    buffer.arm(container, {
      shouldBuffer: () => isInkTool(toolRef.current),
      autoActivate: {
        // Read mode + a pen landing ON a page (not the empty margin/gap around it,
        // where nothing could be inked) → start writing. A pen tap in the margin
        // stays inert instead of silently flipping the doc into edit mode (F1.4).
        shouldActivate: (e) =>
          toolRef.current === "none" &&
          !!(e.target as HTMLElement | null)?.closest?.("[data-page]"),
        onActivate: () => useAnnotationTool.getState().setTool(rememberedTool(docIdRef.current)),
      },
    });
  };

  // Continuous pinch/pan/zoom + wheel + keyboard, driving the controller. A lone
  // finger navigates unless finger-drawing is the intent (editing, finger-draw on):
  // both hooks + the editor consult the SAME input policy so they can't disagree.
  useViewportGestures(containerRef, controllerRef, {
    shouldFingerDraw: () => fingerDrawActive(editModeRef.current),
    // Two/three-finger tap = undo/redo on tablets — the touch analogue of Ctrl+Z/Y,
    // gated like it (edit mode only, read live via refs).
    onTwoFingerTap: () => {
      if (editModeRef.current) modelRef.current.undo();
    },
    onThreeFingerTap: () => {
      if (editModeRef.current) modelRef.current.redo();
    },
  });

  // (Re)draw the read-only overlay on every rendered page from the MODEL (the
  // single source of truth), so a fresh stroke is never absent from a rebuilt
  // overlay. Skipped in edit mode (Fabric is showing). Isolated in try/catch.
  /** Hide EVERY page's read overlay, before Fabric has loaded and before any
   *  editor exists. drawOverlays keeps the overlay for a page with no live editor,
   *  which is right afterwards and wrong here — see the same pair in CanvasView. */
  const clearOverlays = () => {
    const world = worldRef.current;
    if (!world) return;
    for (const pageEl of world.querySelectorAll<HTMLElement>(".pdf-page")) {
      try {
        drawPageAnnotations(pageEl, []);
      } catch {
        /* never let markup break the reader */
      }
    }
  };

  const drawOverlays = () => {
    const world = worldRef.current;
    if (!world) return;
    const edit = editModeRef.current;
    for (const pageEl of world.querySelectorAll<HTMLElement>(".pdf-page")) {
      // Progressive render (Bug B): in READ mode only build the ink SVG for pages
      // on/near screen, so opening a doc with thousands of strokes doesn't render
      // every page synchronously. Off-screen pages get their overlay when they
      // scroll into view (fillVisiblePages / rasterizePage).
      // The OVERLAY band, which is far wider than the editor band: a page that just
      // lost its editor must still be inside it, or its ink vanishes mid-scroll.
      if (!pageInViewport(pageEl, OVERLAY_BAND_MARGIN_PX)) continue;
      const n = Number(pageEl.dataset.page);
      // In EDIT mode only a page with a LIVE editor clears its overlay, because
      // Fabric is drawing that one. Editors exist only for the viewport band now,
      // so every other page keeps its SVG and its ink stays on screen.
      const live = edit && editorsRef.current.has(n);
      try {
        drawPageAnnotations(pageEl, live ? [] : modelRef.current.byPage(n));
      } catch {
        /* never let markup break the reader */
      }
    }
  };

  // Feed the saved-markup fetch into the model: SEED on the first load, RECONCILE
  // after (merge unseen rows / refresh untouched ones, never remove local ink),
  // then redraw the read-only overlay from the model. Declared BEFORE the
  // editor-mount effect so the model is seeded when editors read byPage().
  useEffect(() => {
    if (annotationsLoading) return;
    if (!model.isSeeded()) model.seed(annotations);
    else model.reconcile(annotations);
    if (status === "ready") drawOverlays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotations, annotationsLoading, status, model]);

  // Route model events to the owning page editor while editing (an undo/redo or a
  // reconcile the editor didn't originate), or redraw the read-only overlay while
  // reading. The editor already reflects its OWN mutations, so those emit nothing.
  useEffect(() => {
    const editorFor = (page: number) => editorsRef.current.get(page) ?? null;
    const offs = [
      model.on("added", (a) => {
        if (editModeRef.current) editorFor(a.page)?.applyExternalAdd(a);
        else drawOverlays();
      }),
      model.on("removed", (id, page) => {
        if (editModeRef.current) editorFor(page)?.applyExternalRemove(id);
        else drawOverlays();
      }),
      model.on("replaced", (a) => {
        if (editModeRef.current) editorFor(a.page)?.applyExternalReplace(a);
        else drawOverlays();
      }),
      model.on("idChanged", (tempId, serverId, page) => {
        if (editModeRef.current) editorFor(page)?.applyIdChange(tempId, serverId);
      }),
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  // Arm the pen-down buffer for the whole annotatable session — in READ mode so
  // the first pen contact auto-activates the remembered tool (F1.4), and through
  // edit mode so the first-edit dead zone is bridged (F1.3). Keyed on `editable`
  // only (NOT editMode), so the read→edit transition the pen-down itself triggers
  // never disarms the buffer out from under the stroke it just recorded. The
  // buffer self-disarms after replay; the editor-teardown effect re-arms it.
  useEffect(() => {
    const container = containerRef.current;
    const buffer = penBufferRef.current;
    if (!editable || !persist || !container || !buffer) return;
    armPenBuffer();
    return () => buffer.disarm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, persist]);

  // Mount the Fabric editors ONCE per edit-mode entry, and tear them down only on
  // exit/unmount. Crucially it no longer depends on the raster version: a zoom
  // commit resizes the LIVING editors in place (see renderAll), so a stroke in
  // flight is never destroyed under the pen (north-star A). Lazy-loaded and fully
  // guarded: if Fabric fails to load, we toast, drop back to reading mode, and
  // the PDF + read-only overlay keep working.
  useEffect(() => {
    // Wait for the first layout (page divs exist) AND the saved markup before
    // mounting editors, else entering edit mode before either resolves seeds them
    // empty and existing markup is invisible while editing (the read-only overlay
    // is off in edit mode).
    if (!editMode || status !== "ready" || annotationsLoading || !layoutReady) return;
    let cancelled = false;
    const world = worldRef.current;
    if (!world || !persist) return;

    clearOverlays(); // hide every read-only SVG before Fabric takes over
    loadFabric()
      .then((fabric) => {
        if (cancelled) return;
        fabricRef.current = fabric;
        // Only the viewport band gets a live editor — the same band the raster and
        // the overlay already use. A 200-page PDF used to mount 200 editors and 600
        // canvas bitmaps on the click that enters write mode.
        reconcileEditors();
        // Editors are live — replay any pen contact buffered during the dead zone.
        penBufferRef.current?.attachEditors(editorAtClientPoint);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Couldn't load the annotation tools", {
          description: "Reading still works — try again in a moment.",
        });
        useAnnotationTool.getState().setTool("none");
      });

    return () => {
      cancelled = true;
      fabricRef.current = null;
      if (reconcileTimerRef.current !== undefined) {
        clearTimeout(reconcileTimerRef.current);
        reconcileTimerRef.current = undefined;
      }
      for (const ed of editorsRef.current.values()) ed.destroy();
      editorsRef.current.clear();
      // Restore the read-only overlay now that Fabric is gone.
      drawOverlays();
      // The buffer self-disarmed after its replay; re-arm it so a return to read
      // mode can auto-activate on the next pen contact (F1.4).
      armPenBuffer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, status, annotationsLoading, layoutReady]);

  // Push tool/style changes to the live editors, one field per effect. This list
  // used to be a third copy that omitted setShapeStyle, so a shape fill, dash or
  // corner change never reached a PDF page (WE-08).
  useEditorToolSync(() => editorsRef.current.values(), editMode, style);

  // Fit page width to the viewport (the default zoom). Computed from page 1's
  // unscaled width vs. the available width.
  const fitWidth = async (doc: pdfjs.PDFDocumentProxy): Promise<number | null> => {
    const root = containerRef.current;
    if (!root || !root.clientWidth) return null;
    const pdfPage = await doc.getPage(1);
    const base = pdfPage.getViewport({ scale: 1 }).width;
    const avail = root.clientWidth - 32; // a little breathing room either side
    return base > 0 ? +Math.min(MAX_SCALE, Math.max(MIN_SCALE, avail / base)).toFixed(3) : null;
  };

  // Re-fit to the current viewport width. If the fit scale differs from the
  // committed one, request a re-raster; if it is unchanged (e.g. a rotation that
  // kept the width), re-centre directly so the page is never stuck off-centre.
  const refit = () => {
    const doc = docRef.current;
    const root = containerRef.current;
    const controller = controllerRef.current;
    if (!doc || !root || !controller) return;
    void fitWidth(doc).then((f) => {
      if (!f) return;
      fitPendingRef.current = true;
      if (f === rasterScaleRef.current) {
        const world = worldRef.current;
        const maxW = world?.offsetWidth ?? 0;
        controller.setViewportSize(root.clientWidth, root.clientHeight);
        controller.setContentSize(maxW, world?.scrollHeight ?? 0);
        controller.seat((root.clientWidth - maxW) / 2, TOP_GAP, f);
        fitPendingRef.current = false;
      } else {
        setRasterScale(f);
      }
    });
  };

  // Attach the controller to the world element + seed the viewport size once the
  // container exists.
  useEffect(() => {
    const controller = controllerRef.current;
    const root = containerRef.current;
    if (!controller || !root) return;
    controller.attach(worldRef.current);
    controller.setViewportSize(root.clientWidth, root.clientHeight);
    return () => {
      // Persist the final view state + force the debounced write out on unmount so
      // a tab switch / close doesn't lose the last zoom/camera (F8).
      const c = controllerRef.current;
      if (c) {
        saveViewState(docIdRef.current, {
          zoom: c.scale, tx: c.tx, ty: c.ty, tool: toolRef.current, updatedAt: Date.now(),
        });
      }
      flushViewState();
      clearTimeout(fillTimerRef.current);
      for (const t of renderTasksRef.current) {
        try {
          t.cancel();
        } catch {
          /* best effort */
        }
      }
      renderTasksRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the document once per url, with a brief retry. The bytes are served
  // same-origin and authenticated by the studio's id-token cookie, which can land
  // a beat after a reopen — so a first transient/401 failure self-heals on retry.
  // We never fall back to the OCR text: this IS a PDF.
  useEffect(() => {
    let cancelled = false;
    let task: ReturnType<typeof pdfjs.getDocument> | null = null;
    setStatus("loading");
    // Restore the per-doc view state (F8): a saved zoom means the user took manual
    // control, so skip auto-fit and seat at the saved camera at first layout.
    const saved = loadViewState(docId);
    restoreRef.current = saved;
    autoFitRef.current = !saved;
    fitPendingRef.current = true;
    // New document: its editors must re-seed after ITS first layout, and the old
    // doc's page divs are cleared by the next renderAll (see `fresh`).
    layoutReadyRef.current = false;
    setLayoutReady(false);

    const attempt = async (triesLeft: number): Promise<void> => {
      // Ensure the cookie is fresh before the load — pdf.js issues many
      // byte-range requests over the document's lifetime that we can't retry
      // once the worker owns them, so the token must be valid going in.
      await ensureFreshToken();
      if (cancelled) return;
      task = pdfjs.getDocument({ url, withCredentials: true });
      try {
        const doc = await task.promise;
        if (cancelled) return;
        docRef.current = doc;
        setNumPages(doc.numPages);
        // Cache intrinsic page sizes once so commits can lay out + pick visible
        // pages without rasterizing. (getPage is cached, warming the render path.)
        const sizes: { w: number; h: number }[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const vp = (await doc.getPage(i)).getViewport({ scale: 1 });
          if (cancelled) return;
          sizes.push({ w: vp.width, h: vp.height });
        }
        pageSizesRef.current = sizes;
        // Default to fit-width before the first render so pages open full-width —
        // unless a saved zoom is being restored, which raster at that scale instead.
        const fit = await fitWidth(doc);
        if (cancelled) return;
        if (saved) setRasterScale(clamp(saved.zoom, MIN_SCALE, MAX_SCALE));
        else if (fit) setRasterScale(fit);
        setStatus("ready");
      } catch {
        if (cancelled) return;
        if (triesLeft > 0) {
          // A first failure is most likely a cookie that hasn't landed yet
          // (Firebase re-init) or just expired — refresh before retrying.
          await forceRefresh();
          await new Promise((r) => setTimeout(r, 400));
          if (!cancelled) await attempt(triesLeft - 1);
        } else {
          setStatus("error");
        }
      }
    };
    void attempt(3);

    return () => {
      cancelled = true;
      void task?.destroy();
      docRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // (Re)render every page whenever the document or the COMMITTED scale changes
  // (not the live scale — that is the cheap CSS transform). After the pages
  // exist, size the world, hand the content box to the controller, and either
  // re-centre at fit width or fold the zoom delta back to a crisp factor of 1.
  useEffect(() => {
    const doc = docRef.current;
    const world = worldRef.current;
    const root = containerRef.current;
    const controller = controllerRef.current;
    if (!doc || !world || !root || !controller || status !== "ready") return;
    let cancelled = false;
    const scale = rasterScaleRef.current;
    // A superseded scale's renders must stop, or they stall the worker and delay
    // the new sharpen (the cause of the slow re-raster after a pinch).
    for (const t of renderTasksRef.current) {
      try {
        t.cancel();
      } catch {
        /* best effort */
      }
    }
    renderTasksRef.current.clear();
    renderingRef.current.clear();

    const renderAll = async () => {
      // Lay out every page from cached sizes (cheap, no rasterizing), then
      // RECONCILE the page divs to that layout — reuse a div keyed by page number,
      // create only missing ones, and swap only the raster canvas inside. This is
      // what keeps the long-lived Fabric editors attached across a commit
      // (north-star A); the old code's `world.replaceChildren` tore them all down.
      const sizes = pageSizesRef.current;
      const GAP = 16; // matches .pdf-page mb-4
      const V_MARGIN = 320; // pre-render a little beyond the viewport
      const { layout, maxW, contentH } = computePageLayout(sizes, doc.numPages, scale, GAP);

      // The first layout for a document (fresh, after a url change): the previous
      // doc's page divs are meaningless now, so drop them and rebuild from empty.
      // A same-doc re-raster keeps every div (its editor stays attached).
      const fresh = !layoutReadyRef.current;
      if (fresh) for (const el of [...world.querySelectorAll<HTMLElement>(".pdf-page")]) el.remove();

      // Reconcile: index the surviving divs, remove any extras (only a page-count
      // change reaches here), then reuse-or-create one div per page IN ORDER.
      const byPage = new Map<number, HTMLElement>();
      for (const el of world.querySelectorAll<HTMLElement>(".pdf-page")) byPage.set(Number(el.dataset.page), el);
      for (const n of reconcilePageList([...byPage.keys()], doc.numPages).remove) {
        // Destroy the page's editor before dropping its div, or its window/
        // document listeners leak with the detached DOM (a page-count shrink only
        // reaches here on a same-doc re-raster; a new document rebuilds via `fresh`).
        editorsRef.current.get(n)?.destroy();
        editorsRef.current.delete(n);
        byPage.get(n)?.remove();
        byPage.delete(n);
      }
      const boxes = layout.map((p) => {
        let el = byPage.get(p.n);
        if (!el) {
          el = document.createElement("div");
          el.className = "pdf-page";
          el.dataset.page = String(p.n);
          el.style.width = `${p.w}px`;
          el.style.height = `${p.h}px`;
          el.style.setProperty("--scale-factor", String(scale));
          world.appendChild(el); // in page order (layout is 1..n; extras are gone)
        }
        return { ...p, el };
      });

      // Which pages fall in the viewport at the post-commit transform (factor 1)?
      const ty = fitPendingRef.current ? TOP_GAP : controller.ty;
      const vh = root.clientHeight;
      const visible = boxes.filter((p) => ty + p.top < vh + V_MARGIN && ty + p.top + p.h > -V_MARGIN);

      // Rasterize the visible pages FIRST (at the new scale) so the fold reveals
      // sharp content. rasterizePage swaps the new canvas + text layer into the
      // live div; existing divs are still at the OLD size during this await, but a
      // raster canvas fills its div (style 100%), so it just shows sharp-scaled.
      for (const p of visible) {
        if (cancelled) return;
        await rasterizePage(p.el, p.n, scale);
      }
      if (cancelled) return;

      // Fold: in ONE synchronous tick resize every div to the new scale AND
      // collapse the CSS transform factor to 1 (commitRaster). Screen size depends
      // only on controller.scale + tx/ty (never rasterScale), so the two changes
      // cancel and nothing visually jumps. A page NOT re-rastered keeps its stale
      // canvas (soft-fills the resized div), but its stale text layer would now be
      // MISALIGNED — drop it; fillVisiblePages re-renders it sharp with a fresh one.
      for (const b of boxes) {
        b.el.style.width = `${b.w}px`;
        b.el.style.height = `${b.h}px`;
        b.el.style.setProperty("--scale-factor", String(scale));
        if (b.el.dataset.rscale !== String(scale)) b.el.querySelector(":scope > .textLayer")?.remove();
      }
      world.style.width = `${maxW}px`;
      controller.setViewportSize(root.clientWidth, root.clientHeight);
      controller.setContentSize(maxW, contentH);
      let commitPending = true;
      if (fitPendingRef.current) {
        // Restore the saved camera at first layout (F8); otherwise centre at fit.
        const restore = restoreRef.current;
        if (restore) {
          controller.seat(restore.tx, restore.ty, scale);
          restoreRef.current = null;
        } else {
          controller.seat((root.clientWidth - maxW) / 2, TOP_GAP, scale);
        }
        fitPendingRef.current = false;
        commitPending = false;
      }
      // Living editors keep their seed coordinates; resize each to the new page
      // pixel size — a Fabric viewport zoom, no rebuild, no ink re-inflation.
      for (const b of boxes) editorsRef.current.get(b.n)?.resize(b.w, b.h);
      // AFTER the resize, never before — see the same call in CanvasView. Dropping
      // the compensating CSS magnification first reports a current raster while the
      // editors are still re-tessellating.
      if (commitPending) controller.commitRaster(scale);
      // A new scale changes which pages fall in the band.
      scheduleReconcileRef.current();
      // Read-only markup now that pages are attached (drawPageAnnotations measures
      // clientWidth, 0 while detached). Independent of the canvas, so it renders
      // correctly even on the not-yet-rasterized placeholders.
      drawOverlays();
      updatePageIndicator();
      updateScrollbars();
      // First layout for this document is done → editors may mount onto real DOM.
      if (!layoutReadyRef.current) {
        layoutReadyRef.current = true;
        setLayoutReady(true);
      }
      // Catch anything just outside the pre-render band (and let pan do the rest).
      fillVisiblePages();
    };
    void renderAll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, rasterScale]);

  // Keep the controller's viewport size current and re-fit to the FINAL width
  // once layout settles (a hidden tab becoming visible, the assistant
  // collapsing, a tablet rotating). Re-fit is skipped once the user takes manual
  // zoom control.
  useEffect(() => {
    const root = containerRef.current;
    const controller = controllerRef.current;
    if (!root || !controller) return;
    let t: ReturnType<typeof setTimeout> | undefined;
    const ro = new ResizeObserver(() => {
      // Center-anchored resize compensation (Part A mechanism 2): when a bar
      // toggles and the container resizes, keep the world point at the viewport
      // centre fixed instead of letting it drift with the container edge. Skip it
      // while auto-fitting — there the page deliberately re-fits to the new width
      // (refit re-centres), so preserving the old centre would fight that.
      controller.resizeViewport(root.clientWidth, root.clientHeight, !autoFitRef.current);
      if (!docRef.current || !autoFitRef.current || !root.clientWidth) return;
      clearTimeout(t);
      t = setTimeout(() => {
        if (autoFitRef.current) refit();
      }, 120);
    });
    ro.observe(root);
    return () => {
      clearTimeout(t);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Report the current text selection (and which page it's on) on mouse-up.
  // Suppressed while annotating: a pen writing letters must never grab text.
  const handleMouseUp = () => {
    if (editModeRef.current) return;
    const sel = window.getSelection();
    const root = containerRef.current;
    if (!sel || sel.isCollapsed || !root) return onSelectRef.current(null);
    const text = sel.toString().trim();
    if (text.length < 4 || !root.contains(sel.anchorNode)) return onSelectRef.current(null);
    let el: Node | null = sel.anchorNode;
    while (el && el !== root && !(el instanceof HTMLElement && el.dataset.page)) {
      el = el.parentNode;
    }
    const pageNo = el instanceof HTMLElement && el.dataset.page ? Number(el.dataset.page) : 1;
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    const box = root.getBoundingClientRect();
    onSelectRef.current({
      text,
      page: pageNo,
      // Viewport-relative; the container is the positioned ancestor (no scroll).
      rect: {
        left: rect.left - box.left + rect.width / 2,
        top: rect.top - box.top,
        width: rect.width,
      },
    });
  };

  const zoomBy = (factor: number) => {
    autoFitRef.current = false;
    controllerRef.current?.zoomTo((controllerRef.current?.scale ?? 1) * factor);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#1b1f1a]">
      {/* Native-style toolbar. FIXED height + no-wrap so entering annotate mode or
          switching tools (which expand the AnnotationToolbar) never changes the
          chrome's height — the toolbar scrolls internally instead of reflowing the
          drawing surface below it (viewport stability, Part A mechanism 1). */}
      <div
        className={`flex h-9 shrink-0 flex-nowrap items-center gap-2 px-3 py-1 text-muted-foreground${chromeless ? " hidden" : ""}`}
      >
        <span className="font-mono text-[11px]">
          {status === "ready" ? `${page} / ${numPages}` : "—"}
        </span>
        {/* Next to the page counter, not at the right edge — the reader's
            floating document-controls island owns that corner and covers
            anything right-aligned here (see CanvasView for the long version). */}
        <div className="ml-3 flex items-center gap-1">
          <Tip label="Fit width">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                autoFitRef.current = true;
                refit();
              }}
              aria-label="Fit width"
            >
              <MoveHorizontal className="h-3.5 w-3.5" />
            </Button>
          </Tip>
          <Tip label="Zoom out">
            <Button variant="ghost" size="icon" onClick={() => zoomBy(1 / 1.2)} aria-label="Zoom out">
              <Minus className="h-3.5 w-3.5" />
            </Button>
          </Tip>
          <span className="w-10 text-center font-mono text-[10px] text-muted-foreground">
            {Math.round(displayScale * 100)}%
          </span>
          <Tip label="Zoom in">
            <Button variant="ghost" size="icon" onClick={() => zoomBy(1.2)} aria-label="Zoom in">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </Tip>
        </div>
      </div>

      {/* Reader region: the drawing surface fills it; the annotation island
          OVERLAYS it (absolute, centred) so no tool interaction ever changes the
          surface's box (Part A mechanism 1 / Part E). */}
      <div className="relative min-h-0 flex-1">
        <div
          ref={containerRef}
          onMouseUp={handleMouseUp}
          tabIndex={0}
          style={{ padding: 0, touchAction: "none" }}
          className={`pdf-scroll absolute inset-0 overflow-hidden outline-none${editMode ? " gv-editing" : ""}`}
        >
          <div ref={worldRef} className="pdf-world" />
          {/* Custom scrollbars (the viewport is transformed, not natively scrolled).
              Positions/sizes are set imperatively by updateScrollbars(). */}
          {scrollbars}
        </div>
        {editable && persist && status === "ready" && <AnnotationToolbar fonts={fonts} model={model} />}
      </div>

      {status === "loading" && (
        <div className="absolute inset-0 grid place-items-center">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      )}
      {status === "error" && (
        <div className="grid h-full place-items-center p-6 text-center">
          <p className="text-xs text-muted-foreground">This file couldn’t be rendered as a PDF.</p>
        </div>
      )}
    </div>
  );
}
